import React from "react";
import { useQuery } from "react-query";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { useModal } from "hooks";
import { client } from "helpers/client";

import EmptyIcon from "assets/images/svg/empty_screen.svg";

import Rating from "pages/details/components/rating";
import { Rate } from "pages/details/app-detail.service";
import ReviewDialog from "pages/details/components/review-dialog";
import ReviewCard, { Review } from "pages/details/components/review-card";
import { useAuthContext } from "store/auth";

const PAGE_SIZE = 10;

function Reviews({ appId, rate }: { appId: number; rate?: Rate }) {
  const [{user}]  = useAuthContext();
  const customerId = user.Id;
  const { t } = useTranslation("details");
  const [show, open, close] = useModal(false);
  const [count, setCount] = React.useState(PAGE_SIZE);

  const { data: myReview } = useQuery("my-review", () =>
    client<Review>(`/customerReview/${appId}`).then(
      (r) => r.response
    )
  );

  const { data: communityReviews, isFetching } = useQuery(
    ["community-reviews", count],
    () =>
      client<{ totalCount: number; items: Review[] }>(
        "/customerReview/getPagedList",
        {
          params: {
            appId,
            customerId,
            MaxResultCount: count,
          },
        }
      ).then((r) => r.response)
  );
  return (
    <div className="reviews">
      <h1 className="title">{t("appDetails.reviews")}</h1>
      <div className="review-summary">
        <div className="product-rating">
          <h3 className="rate-num">{rate?.averageRate?.toFixed(1) ?? 0}</h3>
          <Rating rating={rate?.averageRate ?? 0} readOnly />
          <p>{t("appDetails.productRating")}</p>
        </div>
        <div className="review-bars">
          <ReviewBar
            rating={5}
            percentage={rate?.stars?.find((s) => s.star === 5)?.percentage}
          />
          <ReviewBar
            rating={4}
            percentage={rate?.stars?.find((s) => s.star === 4)?.percentage}
          />
          <ReviewBar
            rating={3}
            percentage={rate?.stars?.find((s) => s.star === 3)?.percentage}
          />
          <ReviewBar
            rating={2}
            percentage={rate?.stars?.find((s) => s.star === 2)?.percentage}
          />
          <ReviewBar
            rating={1}
            percentage={rate?.stars?.find((s) => s.star === 1)?.percentage}
          />
        </div>
      </div>
      {myReview ? null : (
        <button onClick={open} className="btn-blue btn-orange">
          {t("appDetails.writeReview")}
        </button>
      )}
      <ReviewDialog appId={appId} onHide={close} show={show} />
      {myReview && (
        <div className="myReview">
          <h1 className="title">{t("appDetails.myReview")}</h1>
          <ReviewCard review={myReview} showControls />
        </div>
      )}
      <div className="myReview community">
        <h1 className="title">
          {t("appDetails.communityFeedback")} ({communityReviews?.totalCount})
        </h1>
        {communityReviews?.totalCount ? (
          <>
            {communityReviews?.items.map((r) => (
              <ReviewCard review={r} key={r.customerName} />
            ))}
            {communityReviews.totalCount > count && (
              <span
                className="full"
                onClick={() => setCount((c) => c + PAGE_SIZE)}
              >
                show more reviews
              </span>
            )}
          </>
        ) : isFetching ? (
          <Spinner animation="grow" />
        ) : (
          <div className="noData">
            <img src={EmptyIcon} alt="no-reviews" />
            <h2>{t("appDetails.noReviews")}</h2>
          </div>
        )}
      </div>
    </div>
  );
}

export function ReviewBar({ percentage = 0, rating = 5 }) {
  return (
    <div className="review-bar">
      <progress
        value={percentage * 100}
        max={100}
        className="progress-review"
      />
      <div className="stars">
        <Rating rating={rating} readOnly />
        <p>{(percentage * 100).toFixed(1)}%</p>
      </div>
    </div>
  );
}

export default Reviews;
